import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { heroSlides } from "../data/siteData";

export default function HeroSlider() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = heroSlides.length;

  const go = useCallback((d) => setI((p) => (p + d + n) % n), [n]);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => go(1), 5000);
    return () => clearTimeout(t);
  }, [i, paused, go]);

  return (
    <section
      className="relative h-[330px] w-full overflow-hidden bg-[#0c2a66] sm:h-[430px] lg:h-[598px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Featured projects"
    >
      {heroSlides.map((s, idx) => (
        <div
          key={s.headline}
          className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
            idx === i ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={s.image}
              alt={s.headline}
              className={`h-full w-full object-cover ${idx === i ? "mr-kenburns" : ""}`}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/18 to-black/32" />
          <div className="relative flex h-full items-center">
            <div className="mr-container-wide text-center">
              {idx === 0 ? (
                <h1 className="font-head mx-auto max-w-3xl text-[26px]/[1.3] font-extrabold text-white drop-shadow-[0_2px_12px_rgba(0,0,0,.8)] sm:text-4xl/[1.3] lg:text-[46px]/[1.3]">
                  <Link to={s.to} className="transition-colors hover:text-[#cfe0ff]">
                    {s.headline}
                  </Link>
                </h1>
              ) : (
                <h2 className="font-head mx-auto max-w-3xl text-[26px]/[1.3] font-extrabold text-white drop-shadow-[0_2px_12px_rgba(0,0,0,.8)] sm:text-4xl/[1.3] lg:text-[46px]/[1.3]">
                  <Link to={s.to} className="transition-colors hover:text-[#cfe0ff]">
                    {s.headline}
                  </Link>
                </h2>
              )}
              <Link
                to={s.to}
                className="mt-7 inline-block bg-[#244aa2] px-8 py-3.5 text-[13px] font-bold uppercase tracking-[0.1em] text-white shadow-lg transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#0c2a66]"
              >
                {s.cta}
              </Link>
            </div>
          </div>
        </div>
      ))}

      <button
        onClick={() => go(-1)}
        aria-label="Previous slide"
        className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/35 p-2.5 text-white transition-colors duration-200 hover:bg-[#244aa2] sm:left-5"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        onClick={() => go(1)}
        aria-label="Next slide"
        className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/35 p-2.5 text-white transition-colors duration-200 hover:bg-[#244aa2] sm:right-5"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-2.5">
        {heroSlides.map((s, idx) => (
          <button
            key={s.headline}
            onClick={() => setI(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              idx === i ? "w-8 bg-white" : "w-2.5 bg-white/55 hover:bg-white/85"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
